import * as React from 'react'
import { useEffect, useRef, useState } from 'react'

import { useHotBuyOrdersList } from './hooks'
import { HotBuyOrder } from './types'
import { CButton, CCardBody } from '@coreui/react-pro'
import { Badge, InputRef, Select, Table, Tag } from 'antd'
import { path, role } from '../../../../libs'
import { ColumnsType } from 'antd/lib/table'
import { FilterValue } from 'antd/es/table/interface'
import { useStore } from 'effector-react'
import { $userRole } from '../../../../features/user'
import {
  getColumnSearchDateProps,
  getColumnSearchProps,
} from '../../tableFunctions'
import {
  backendStatusFilter,
  convertOrderStatus,
  paymentMethod,
  statusFilter,
} from './consts'
import { renderHotBuyOrderItems } from './orderItems'
import Title from 'antd/lib/typography/Title'
import dayjs from 'dayjs'
import { HotBuyOrdersPage } from '../page'
import {
  $hotBuyOrdersWithoutPagination,
  hotBuyOrdersListWithoutPaginationMounted,
} from './model'

export const HotBuyOrderList = () => {
  const {
    hotBuyOrders,
    totalCountOfHotBuyOrders,
    loading,
    onChangeHotBuyOrdersList,
  } = useHotBuyOrdersList()

  const hotBuyOrdersWithoutPagination = useStore($hotBuyOrdersWithoutPagination)

  const [isCalendarOpen, setIsCalendarOpen] = useState(false)
  const [searchText, setSearchText] = useState<{
    [key: string]: string
  }>({
    // @ts-ignore
    // createdAt: [dayjs().add(-7, 'd'), dayjs()],
  })
  const [searchedColumn, setSearchedColumn] = useState('')
  const [orderBy, setOrderBy] = useState('id')
  const [orderDirection, setOrderDirection] = useState('-')
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [filtersState, setFiltersState] = useState<
    Record<string, FilterValue | null>
  >({
    // status: ['completed'],
    // gatewayId:['>0'],
  })

  const userRole = useStore($userRole)

  const searchInput = useRef<InputRef>(null)

  const handleTableChange = ({
    currentPage,
    currentPageSize,
    currentOrderBy,
    currentOrderDirection,
    filters,
  }: {
    currentPage?: number
    currentPageSize?: number
    currentOrderBy?: string
    currentOrderDirection?: string
    filters?: Record<string, FilterValue | null>
  }) => {
    if (currentPage) {
      setPage(currentPage)
    }
    if (currentPageSize) {
      setPageSize(currentPageSize)
    }
    if (currentOrderBy) {
      setOrderBy(currentOrderBy)
    }
    if (currentOrderDirection) {
      if (currentOrderDirection == 'descend') {
        setOrderDirection('-')
      } else {
        setOrderDirection('')
      }
    }

    if (filters) {
      setFiltersState(filters)
    }
  }

  useEffect(() => {
    hotBuyOrdersListWithoutPaginationMounted()
  }, [])

  useEffect(() => {
    onChangeHotBuyOrdersList({
      limit: pageSize,
      page: page,
      orderBy: orderBy,
      orderDirection: orderDirection,
      searchText: searchText,
      filters: filtersState,
    })
  }, [pageSize, page, orderBy, orderDirection, searchText, filtersState])

  const getCsvCode = () => {
    if (hotBuyOrdersWithoutPagination.length == 0) {
      return 'data:text/csv;charset=utf-8,'
    }
    const csvContent = hotBuyOrdersWithoutPagination.map((item) => {
      return {
        ID: item.id,
        'Created at': new Date(item.addedAt).toLocaleString().replace(',', ' '),
        'User ID': item.clientId,
        Amount: `${+item.amount} usd`,
        'Total sell amount': `${+item.totalSellAmount} usd`,
        'NOVA commission $': `${+item.totalPriceFee} usd`,
        'Order details': `"${item.items
          .map(
            (item) =>
              `Sell offer ID:${item.SellOfferID}\nProperty Id: ${item.PropertyID}\nName: ${item.PropertyName}\nAmount: ${item.TokenAmount}\nToken price: ${item.TokenPriceTaker} USD\nBuyer price: ${item.TotalPriceTaker} USD\nSeller price: ${item.TotalPriceMaker} USD\nNova commission: ${item.TotalPriceFee} USD / ${item.TokenPriceFeePercent}%`,
          )
          .join('\n')}"`,
      }
    })
    const header = Object.keys(csvContent[0])?.join(',')
    const data = csvContent.map((item) => Object.values(item).join(','))
    return (
      'data:text/csv;charset=utf-8, ' +
      encodeURIComponent([header, ...data].join('\n'))
    )
  }

  const columns: ColumnsType<HotBuyOrder> = [
    {
      title: 'ID',
      dataIndex: 'id',
      ...getColumnSearchProps<HotBuyOrder>(
        'id',
        setSearchText,
        searchText,
        searchInput,
      ),
      sorter: (a, b) => parseInt(a.id) - parseInt(b.id),
      defaultSortOrder: 'descend',
    },
    {
      title: 'Created at',
      dataIndex: 'addedAt',
      ...getColumnSearchDateProps<HotBuyOrder>(
        'addedAt',
        setSearchText,
        searchText,
        setIsCalendarOpen,
        isCalendarOpen,
        searchInput,
      ),
      render: (item) => <div>{new Date(item).toLocaleString()}</div>,
      sorter: true,
      width: '150px',
    },
    {
      title: 'User ID',
      ...getColumnSearchProps<HotBuyOrder>(
        'clientId',
        setSearchText,
        searchText,
        searchInput,
      ),
      render: (item: HotBuyOrder) => (
        <div>
          {item.user?.isTest ? (
            <Badge count={'Test'} offset={[10, -10]}>
              <a
                href={path.clients.details(String(item.clientId))}
                target="_blank"
              >
                {item.clientId}
              </a>
            </Badge>
          ) : (
            <a
              href={path.clients.details(String(item.clientId))}
              target="_blank"
            >
              {item.clientId}
            </a>
          )}
        </div>
      ),
    },
    {
      title: 'Amount',
      dataIndex: 'amount',
      render: (item) => <div>{+item + ' USD'}</div>,
    },
    {
      title: 'Total sell amount',
      dataIndex: 'totalSellAmount',
      render: (item) => <div>{+item + ' USD'}</div>,
    },
    {
      title: 'NOVA commission $',
      dataIndex: 'totalPriceFee',
      render: (item) => <div>{+item + ' USD'}</div>,
    },
    {
      title: 'Order details',
      dataIndex: 'items',
      render: renderHotBuyOrderItems,
    },
    {
      title: 'Expired At',
      dataIndex: 'expiredAt',
      render: (item) => <div>{new Date(item).toLocaleString()}</div>,
      width: '150px',
    },
    {
      title: 'Status',
      dataIndex: 'status',
    },
    {
      title: 'Status Message',
      dataIndex: 'statusMsg',
    },
    {
      title: 'Backend Status',
      dataIndex: 'backendStatus',
    },
    {
      title: 'Transaction Request ID',
      dataIndex: 'transactionRequestId',
      render: (item) => (
        <a href={path.billing.deposit.edit(item)} target="_blank">
          {item}
        </a>
      ),
    },
  ]

  const isDisplayDownloadButton =
    userRole?.filter((roleItem) =>
      [
        role.root(),
        role.head.cfo(),
        role.finances.financeManager(),
        role.nova(),
      ].includes(roleItem),
    ).length !== 0

  return (
    <CCardBody>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          marginBottom: '20px',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-end',
          }}
        >
          {isDisplayDownloadButton && (
            <CButton
              color="primary"
              href={getCsvCode()}
              download={
                'orders ' +
                dayjs(Date.now()).format('YYYY-MM-DD HH-mm') +
                '.csv'
              }
              target="_blank"
            >
              Download current items (.csv)
            </CButton>
          )}
        </div>
      </div>
      <Table
        dataSource={hotBuyOrders}
        bordered={true}
        scroll={{ x: true }}
        loading={loading}
        onRow={(record, rowIndex) => {
          return {
            onClick: (event) => {},
          }
        }}
        rowKey="id"
        columns={columns}
        pagination={{
          showSizeChanger: true,
          total: totalCountOfHotBuyOrders,
        }}
        onChange={(pagination, filters, sorter, extra) =>
          handleTableChange({
            //@ts-ignore
            currentPage: pagination.current, //@ts-ignore
            currentPageSize: pagination.pageSize, //@ts-ignore
            currentOrderDirection: sorter.order, //@ts-ignore
            currentOrderBy: sorter.field,
            filters: { ...filtersState, ...filters },
          })
        }
      />
    </CCardBody>
  )
}
